import { __extends } from "tslib";
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * EffectRimLightMethod provides a method to add rim lighting to a material.
 * This adds a glow-like effect to edges of objects.
 */
var EffectRimLightMethod = /** @class */ (function (_super) {
    __extends(EffectRimLightMethod, _super);
    /**
     * Creates a new <code>EffectRimLightMethod</code> object.
     *
     * @param color The colour of the rim light.
     * @param strength The strength of the rim light.
     * @param power The power of the rim light. Higher values will result in a higher edge fall-off.
     * @param blend The blend mode with which to add the light to the object.
     */
    function EffectRimLightMethod(color, strength, power, blend) {
        if (color === void 0) { color = 0xffffff; }
        if (strength === void 0) { strength = .4; }
        if (power === void 0) { power = 2; }
        if (blend === void 0) { blend = 'mix'; }
        var _this = _super.call(this) || this;
        _this._blendMode = blend;
        _this._strength = strength;
        _this._power = power;
        _this._color = color;
        return _this;
    }
    Object.defineProperty(EffectRimLightMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectRimLightMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectRimLightMethod.prototype, "color", {
        /**
         * The color of the rim light.
         */
        get: function () {
            return this._color;
        },
        set: function (value) {
            if (this._power == value)
                return;
            this._color = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectRimLightMethod.prototype, "strength", {
        /**
         * The strength of the rim light.
         */
        get: function () {
            return this._strength;
        },
        set: function (value) {
            if (this._strength == value)
                return;
            this._strength = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectRimLightMethod.prototype, "power", {
        /**
         * The power of the rim light. Higher values will result in a higher edge fall-off.
         */
        get: function () {
            return this._power;
        },
        set: function (value) {
            if (this._power == value)
                return;
            this._power = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectRimLightMethod.prototype, "blendMode", {
        /**
         * The blend mode with which to add the light to the object.
         *
         * EffectRimLightMethod.MULTIPLY multiplies the rim light with the material's colour.
         * EffectRimLightMethod.ADD adds the rim light with the material's colour.
         * EffectRimLightMethod.MIX provides normal alpha blending.
         */
        get: function () {
            return this._blendMode;
        },
        set: function (value) {
            if (this._blendMode == value)
                return;
            this._blendMode = value;
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    EffectRimLightMethod.ADD = 'add';
    EffectRimLightMethod.MULTIPLY = 'multiply';
    EffectRimLightMethod.MIX = 'mix';
    EffectRimLightMethod.assetType = '[asset EffectRimLightMethod]';
    return EffectRimLightMethod;
}(MethodBase));
export { EffectRimLightMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_EffectRimLightMethod provides a method to add rim lighting to a material. This adds a glow-like effect to edges of objects.
 */
var _Shader_EffectRimLightMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectRimLightMethod, _super);
    function _Shader_EffectRimLightMethod() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Creates a new EffectEnvMapChunk.
     */
    _Shader_EffectRimLightMethod.prototype.init = function (method, shader) {
        _super.prototype.init.call(this, method, shader);
        this._method = method;
        this._shader = shader;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectRimLightMethod.prototype._initVO = function (chunkVO) {
        chunkVO.needsNormals = true;
        chunkVO.needsView = true;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectRimLightMethod.prototype._activate = function () {
        if (this._invalid) {
            var index = this._rimColorIndex;
            var data = this._shader.fragmentConstantData;
            var color = this._method.color;
            data[index] = ((color >> 16) & 0xff) / 0xff;
            data[index + 1] = ((color >> 8) & 0xff) / 0xff;
            data[index + 2] = (color & 0xff) / 0xff;
            data[index + 3] = 1;
            data[index + 4] = this._method.strength;
            data[index + 5] = this._method.power;
        }
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectRimLightMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var dataRegister = registerCache.getFreeFragmentConstant();
        var dataRegister2 = registerCache.getFreeFragmentConstant();
        var temp = registerCache.getFreeFragmentVectorTemp();
        var code = '';
        this._rimColorIndex = dataRegister.index * 4;
        code += 'dp3 ' + temp + '.x, ' + sharedRegisters.viewDirFragment + '.xyz, ' + sharedRegisters.normalFragment + '.xyz\n' +
            'sat ' + temp + '.x, ' + temp + '.x\n' +
            'sub ' + temp + '.x, ' + dataRegister + '.w, ' + temp + '.x\n' +
            'pow ' + temp + '.x, ' + temp + '.x, ' + dataRegister2 + '.y\n' +
            'mul ' + temp + '.x, ' + temp + '.x, ' + dataRegister2 + '.x\n' +
            'sub ' + temp + '.x, ' + dataRegister + '.w, ' + temp + '.x\n' +
            'mul ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + temp + '.x\n' +
            'sub ' + temp + '.w, ' + dataRegister + '.w, ' + temp + '.x\n';
        if (this._method.blendMode == EffectRimLightMethod.ADD) {
            code += 'mul ' + temp + '.xyz, ' + temp + '.w, ' + dataRegister + '.xyz\n' +
                'add ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + temp + '.xyz\n';
        }
        else if (this._method.blendMode == EffectRimLightMethod.MULTIPLY) {
            code += 'mul ' + temp + '.xyz, ' + temp + '.w, ' + dataRegister + '.xyz\n' +
                'mul ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + temp + '.xyz\n';
        }
        else {
            code += 'sub ' + temp + '.xyz, ' + dataRegister + '.xyz, ' + targetReg + '.xyz\n' +
                'mul ' + temp + '.xyz, ' + temp + '.xyz, ' + temp + '.w\n' +
                'add ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + temp + '.xyz\n';
        }
        return code;
    };
    return _Shader_EffectRimLightMethod;
}(_Shader_MethodBase));
export { _Shader_EffectRimLightMethod };
ShaderBase.registerAbstraction(_Shader_EffectRimLightMethod, EffectRimLightMethod);
